import axios from "axios";
import _ from "lodash";
import short from "short-uuid";
import { v4 as uuidv4 } from "uuid";
import Vue from "vue";
import Vuex from "vuex";
import AWS from "../../api/aws_config";
import { dashCase } from "../../api/_lib/utils/dashCase";
import documentCache from "../../api/_lib/utils/documentCache";
Vue.use(Vuex);

// Login/Register store

const auth = {
  namespaced: true,
  state: () => ({
    registerCompletedSuccessFully: false
  }),
  mutations: {
    registerSuccess(state) {
      state.registerCompletedSuccessFully = true;
    },
    setRegisterSuccess(state) {
      state.registerCompletedSuccessFully = false;
    }
  },
  actions: {
    async login({ state, dispatch }, { email, password }) {
      if (state.userToken) {
        await dispatch("cognito/signOut", _, { root: true });
      }

      const { data } = await axios.get("/api/user/cognito", {
        params: { email }
      });

      const { username } = data;

      if (username) {
        const r = await dispatch(
          "cognito/signInUser",
          {
            username,
            password
          },
          { root: true }
        );
        // In actuallity this is the gluidId
        return r.username;
      } else {
        throw new Error("This user does not exist");
      }
    },
    async register(
      { commit, dispatch },
      { email, password, firstName, lastName, gluidId }
    ) {
      const { data } = await axios.get("/api/user/cognito", {
        params: { email: email }
      });
      if (data.username) {
        throw new Error("This email is already in use");
      } else {
        const accountId = short.generate();
        await dispatch(
          "cognito/registerUser",
          {
            username: gluidId,
            password: password,
            attributes: {
              email,
              given_name: firstName,
              name: lastName,
              "custom:accountId": accountId,
              //Has to be preferred_username so that we get an error if it is not unique
              preferred_username: gluidId
            }
          },
          { root: true }
        );
        commit("registerSuccess");
      }
    }
  },
  getters: {
    registerSuccess: (state) => {
      return state.registerCompletedSuccessFully;
    }
  }
};

// Main store
export default new Vuex.Store({
  state: {
    userSpaces: null,
    userToken: null,
    userIdToken: null,
    activeDocument: null,
    activeDocumentTitle: null,
    activeDocumentJsonDoc: null,
    activeSpace: null,
    activeSpaceTree: null,
    activeSpacePaths: null,
    activePath: null
  },
  mutations: {
    setActiveDocumentTitle(state, title) {
      state.activeDocumentTitle = title;
    },
    setAllUserSpacesForUser(state, userSpaces) {
      state.userSpaces = userSpaces;
    },
    setUserToken(state, jwt) {
      state.userToken = jwt;
    },
    setUserIdToken(state, idToken) {
      state.userIdToken = idToken;
    },
    setActiveSpace(state, space) {
      const { documentIdCache, documentPathCache } = documentCache(
        space.tree,
        true
      );
      space.documentIdCache = documentIdCache;
      space.documentPathCache = documentPathCache;
      state.activeSpace = space;
      state.activeSpaceTree = space.tree;
      let activeSpacePaths = [];
      for (const key in documentPathCache) {
        activeSpacePaths.push(key);
      }
      state.activeSpacePaths = activeSpacePaths;
    },
    setActiveSpaceTree(state, tree) {
      state.activeSpace.tree = tree;
      state.activeSpaceTree = tree;
    },
    setActiveDocument(state, document) {
      state.activeDocumentJsonDoc = document.jsonDoc;
      state.activeDocument = document;
    },
    setActivePath(state, path) {
      state.activePath = path;
    },
    setJSONDocumentAtURL(state, { path, jsonDoc }) {
      state.embeddedDocuments[path] = jsonDoc;
    },
    resetApp(state) {
      state.userSpaces = null;
      state.userToken = null;
      state.activeDocument = null;
      state.activeDocumentTitle = null;
      state.activeDocumentJsonDoc = null;
      state.activeSpace = null;
      state.activeSpaceTree = null;
      state.activeSpacePaths = null;
      state.activePath = null;
    }
  },
  actions: {
    async initializeApp({ state, commit }, url) {
      if (`/${state.activePath}` !== url) {
        const r = await axios.get("/api/space/readSpace", {
          headers: { jwt: state.userToken },
          params: { url }
        });
        commit("setActiveSpace", r.data.activeSpace);
        commit("setActiveDocument", r.data.activeDocument);
        commit("setActiveDocumentTitle", r.data.activeDocumentTitle);
      }
    },
    async fetchAllUserSpacesForUser({ state, commit }) {
      const r = await axios.get(`/api/space/readAllSpaces`, {
        headers: { jwt: state.userToken }
      });
      commit("setAllUserSpacesForUser", r.data);
    },

    async createNewSpaceAndDocumentForUser({ state }, title) {
      title = title.trim();
      const slug = dashCase(title);
      if (title) {
        for (const userSpace of state.userSpaces) {
          if (userSpace.slug === slug) {
            throw new Error("This title already exists!");
          }
        }
        return await axios.put(`/api/space/createSpace`, {
          headers: { jwt: state.userToken, userIdToken: state.userIdToken },
          title,
          slug
        });
      } else {
        throw new Error("Please fill in a title!");
      }
    },

    async createSpaceFromFile({ state }, { title, xml, json, gluidId }) {
      title = title.trim();
      const slug = dashCase(title);
      if (title) {
        for (const userSpace of state.userSpaces) {
          if (userSpace.slug === `${gluidId}/${slug}`) {
            throw new Error("This title already exists!");
          }
        }
        try {
          const { data } = await axios.post(`/api/space/createSpaceFormFile`, {
            headers: { jwt: state.userToken, userIdToken: state.userIdToken },
            slug,
            xml,
            json
          });
          console.log("Done with axios call", data);
          return data;
        } catch (err) {
          console.log("There is an error");
          console.error(err);
        }
      } else {
        throw new Error("Please fill in a title!");
      }
    },

    async createNewDocumentForUser({ state, commit }, { parent, title }) {
      title = title.trim();

      let r;

      if (title) {
        if (parent.children) {
          for (let i = 0; i < parent.children.length; i++) {
            const childNode = parent.children[i];
            if (dashCase(childNode.title) === dashCase(title)) {
              throw new Error("This title already exists!");
            }
          }
        }

        r = await axios.put("/api/space/addDocument", {
          headers: { jwt: state.userToken },
          path: parent.path,
          title
        });
      } else {
        throw new Error("Please fill in a title!");
      }
      const {
        activePath,
        activeSpace,
        activeDocument,
        activeDocumentTitle
      } = r.data;

      commit("setActiveSpace", activeSpace);
      commit("setActiveDocument", activeDocument);
      commit("setActiveDocumentTitle", activeDocumentTitle);
      commit("setActivePath", activePath);

      return r.data;
    },

    async saveActiveDocument(
      { state, dispatch, commit },
      { editorHTML, editorJSON, path }
    ) {
      // Saving the document -> we need the updated html and json.
      const documentId = state.activeDocument.documentId;
      await dispatch("fetchAllUserSpacesForUser");
      const userSpaces = state.userSpaces;
      const title = state.activeDocumentTitle;
      const slug = dashCase(title.trim());
      const spaceId = state.activeSpace.spaceId;
      try {
        userSpaces.forEach((userSpace) => {
          if (
            userSpace.slug === slug &&
            userSpace.documentId !== state.activeSpace.tree.documentId
          ) {
            throw new Error("This title is already in use by another space!");
          }
        });

        const r = await axios.put("/api/space/saveDocument", {
          headers: { jwt: state.userToken },
          documentId,
          title,
          editorHTML,
          editorJSON,
          path,
          spaceId
        });

        const {
          activePath,
          activeSpace,
          activeDocument,
          activeDocumentTitle
        } = r.data;

        commit("setActiveSpace", activeSpace);
        commit("setActiveDocument", activeDocument);
        commit("setActiveDocumentTitle", activeDocumentTitle);
        commit("setActivePath", activePath);
      } catch (e) {
        // When the title already exists
        console.log(e);
      }
    },

    async deleteActiveDocument({ state }, path) {
      await axios.delete("/api/space/removeDocument", {
        headers: { jwt: state.userToken },
        data: {
          documentId: state.activeDocument.documentId,
          path
        }
      });
    },

    async moveDocument(
      { state, commit },
      { documentId, newParentPath, newChildIndex }
    ) {
      const { data } = await axios.post("/api/space/moveDocument", {
        headers: { jwt: state.userToken },
        documentId,
        newParentPath,
        newChildIndex
      });
      commit("setActiveSpace", data);
    },

    async fetchJSONDocumentAtURL({ state, commit }, path) {
      const documentId = state.activeSpace.documentPathCache[path].documentId;
      const { data } = await axios("/api/document/readDocument", {
        headers: { jwt: state.userToken },
        params: { documentId }
      });
      let jsonDoc = data;
      commit("setJSONDocumentAtURL", { path, jsonDoc });
    },

    async uploadImage(_, { image }) {
      let file = image;
      let albumPhotosKey = "userupload/image/";
      let photoKey = albumPhotosKey + uuidv4() + image.name;

      let upload = new AWS.S3.ManagedUpload({
        params: {
          Bucket: process.env.VUE_APP_AWS_BUCKET_NAME,
          Key: photoKey,
          Body: file
        }
      });

      let { Location } = await upload.promise();
      return Location;
    },

    async uploadJSON(_, { json }) {
      let file = json;
      let key = "userupload/json/";
      let jsonKey = key + uuidv4() + json.name;

      let upload = new AWS.S3.ManagedUpload({
        params: {
          Bucket: process.env.VUE_APP_AWS_BUCKET_NAME,
          Key: jsonKey,
          Body: file
        }
      });

      let { Location } = await upload.promise();
      return Location;
    }
  },
  getters: {
    userSpaces: (state) => {
      return state.userSpaces;
    },
    activeDocument: (state) => {
      return state.activeDocument;
    },
    activeSpace: (state) => {
      return state.activeSpace;
    },
    activeDocumentJsonDoc: (state) => {
      return state.activeDocumentJsonDoc;
    },
    activeSpaceTree: (state) => {
      return state.activeSpaceTree;
    },
    activePath: (state) => {
      return state.activePath;
    },
    activeSpacePaths: (state) => {
      return state.activeSpacePaths;
    },
    embeddedDocuments: (state) => {
      return state.embeddedDocuments;
    }
  },
  modules: {
    auth
  }
});
